import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

export default function ManualInput({ 
  onSubmit, 
  initialStocks = [],
  submitButtonText = 'Analyze Portfolio',
  showSubmitButton = true,
  onStocksChange = null, // New prop for parent components to track stocks
}) {
  const user = useSelector(state => state.auth.user);
  const [stocks, setStocks] = useState([]);
  const [currentStock, setCurrentStock] = useState({ ticker: '', shares: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialStocks.length > 0) {
      setStocks(initialStocks);
    }
  }, [initialStocks]);

  const handleStockChange = (e) => {
    const { name, value } = e.target;
    setCurrentStock(prev => ({
      ...prev,
      [name]: name === 'ticker' ? value.toUpperCase() : value
    }));
    setError('');
  };

  const validateStock = () => {
    if (!currentStock.ticker.match(/^[A-Z]+$/)) {
      return 'Invalid ticker symbol';
    }
    if (!currentStock.shares || isNaN(currentStock.shares) || Number(currentStock.shares) <= 0) {
      return 'Please enter a valid number of shares';
    }
    if (stocks.some(stock => stock.ticker === currentStock.ticker)) {
      return 'This stock is already in your portfolio';
    }
    return '';
  };

  const handleAddStock = () => {
    const validationError = validateStock();
    if (validationError) {
      setError(validationError);
      return;
    }

    const newStocks = [...stocks, {
      ticker: currentStock.ticker,
      shares: Number(currentStock.shares)
    }];
    
    setStocks(newStocks);
    setCurrentStock({ ticker: '', shares: '' });
    setError('');
    
    if (onStocksChange) {
      onStocksChange(newStocks);
    }
  };

  const handleRemoveStock = (index) => {
    const newStocks = stocks.filter((_, i) => i !== index);
    setStocks(newStocks);
    if (onStocksChange) {
      onStocksChange(newStocks);
    }
  };

  const handleSubmit = async () => {
    if (stocks.length === 0) {
      setError('Please add at least one stock to your portfolio');
      return;
    }

    setIsSubmitting(true);
    try {
      const portfolio = stocks.reduce((acc, stock) => {
        acc[stock.ticker] = Number(stock.shares);
        return acc;
      }, {});

      await onSubmit({
        portfolio,
        email: user?.email
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          name="ticker"
          label="Ticker Symbol"
          value={currentStock.ticker}
          onChange={handleStockChange}
          placeholder="e.g., AAPL"
          size="small"
          fullWidth
          inputProps={{ maxLength: 5 }}
        />
        <TextField
          name="shares"
          label="Shares"
          type="number"
          value={currentStock.shares}
          onChange={handleStockChange}
          size="small"
          fullWidth
          inputProps={{ min: "0", step: "1" }}
        />
        <Button
          variant="contained"
          onClick={handleAddStock}
          disabled={!currentStock.ticker || !currentStock.shares}
          startIcon={<AddIcon />}
          sx={{ 
            minWidth: { xs: '100%', sm: '120px' },
            height: { sm: '40px' }
          }}
        >
          Add
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {stocks.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticker</TableCell>
                <TableCell align="right">Shares</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stocks.map((stock, index) => (
                <TableRow key={index}>
                  <TableCell>{stock.ticker}</TableCell>
                  <TableCell align="right">{stock.shares}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveStock(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showSubmitButton && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={isSubmitting || stocks.length === 0}
          sx={{ mt: 0 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            submitButtonText
          )}
        </Button>
      )}
    </Box>
  );
}